import fs from "node:fs";
import { promises as fsPromises } from "node:fs";
import path from "node:path";
import * as pty from "node-pty";
import type { Logger } from "pino";
import type { AgentRateLimit } from "../../agent-sdk-types.js";
import { findExecutable } from "../../../../utils/executable.js";
import { ensureStatuslineRelayScript, parseStatuslinePayload } from "./statusline-relay.js";

/**
 * Plan-usage probe — warm PTY, on-demand refresh.
 *
 * Spawns ONE interactive `claude` PTY at daemon start with `statusLine.command`
 * wired to our relay. The PTY then sits idle until something calls
 * `triggerProbe()`, at which point we write a tiny prompt — claude does one
 * Haiku turn — the statusline hook fires — relay writes JSON — we parse it
 * and resolve.
 *
 * Why a warm PTY: claude cold-start is 3-5s. A persistent PTY makes
 * `triggerProbe()` finish in ~1-2s, which is what a Usage modal needs.
 *
 * Why no interval: nothing polls in the background. We only probe when
 * a client (modal) asks. Zero tokens spent on idle days.
 *
 * Why NOT `--bare`: per `claude --help`, `--bare` forces auth to
 * ANTHROPIC_API_KEY / apiKeyHelper and explicitly refuses to read OAuth or
 * keychain. OAuth (Claude Pro/Max) is the common subscription path, so a
 * `--bare` PTY silently can't authenticate, never completes a turn, and
 * never fires the statusline. We pay ~50 MB extra idle RAM to keep OAuth
 * working.
 *
 * Public API:
 *   - start(): spawn the warm PTY (no-op if claude binary missing).
 *   - triggerProbe(): write probe prompt, await next statusline payload.
 *   - subscribe(listener): receive each probe's result.
 *   - getLastSnapshot(): synchronous cache read.
 */

const DEFAULT_PROBE_TIMEOUT_MS = 15_000;
const DEFAULT_INITIAL_DELAY_MS = 3_000;
const PROBE_MODEL = "haiku";
const PROBE_PROMPT = "ok";

export type UsageProbeListener = (rateLimits: AgentRateLimit[]) => void;

export interface PlanUsageSnapshot {
  rateLimits: AgentRateLimit[];
  capturedAt: number;
}

export interface ClaudeUsageProbeOptions {
  appostleHome: string;
  logger: Logger;
  /** Override for tests. */
  probeTimeoutMs?: number;
  /** Override for tests — delay between PTY spawn and first probe attempt. */
  initialDelayMs?: number;
}

export class ClaudeUsageProbe {
  private readonly appostleHome: string;
  private readonly logger: Logger;
  private readonly probeTimeoutMs: number;
  private readonly initialDelayMs: number;
  private readonly relayScriptPath: string;
  private readonly outputPath: string;
  private readonly probeCwd: string;

  private ptyProcess: pty.IPty | null = null;
  private watcher: fs.FSWatcher | null = null;
  private ptyReadyAt: number | null = null;
  private lastSnapshot: PlanUsageSnapshot | null = null;
  private inflight: Promise<PlanUsageSnapshot | null> | null = null;
  private readonly listeners = new Set<UsageProbeListener>();
  private starting = false;
  private stopped = false;
  /** Resolved by the dir-watcher when outputPath changes. */
  private pendingResolve: ((snapshot: PlanUsageSnapshot | null) => void) | null = null;

  constructor(opts: ClaudeUsageProbeOptions) {
    this.appostleHome = opts.appostleHome;
    this.logger = opts.logger.child({ module: "claude-usage-probe" });
    this.probeTimeoutMs = opts.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS;
    this.initialDelayMs = opts.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
    this.relayScriptPath = ensureStatuslineRelayScript(this.appostleHome);
    this.outputPath = path.join(this.appostleHome, "internal", "usage-probe.json");
    this.probeCwd = path.join(this.appostleHome, "internal", "probe-cwd");
  }

  getLastRateLimits(): AgentRateLimit[] | null {
    return this.lastSnapshot?.rateLimits ?? null;
  }

  getLastSnapshot(): PlanUsageSnapshot | null {
    return this.lastSnapshot;
  }

  subscribe(listener: UsageProbeListener): () => void {
    this.listeners.add(listener);
    if (this.lastSnapshot) {
      try {
        listener(this.lastSnapshot.rateLimits);
      } catch (err) {
        this.logger.debug({ err }, "usage-probe subscriber threw on initial deliver");
      }
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  async start(): Promise<void> {
    if (this.starting || this.ptyProcess || this.stopped) return;
    this.starting = true;
    try {
      const claudeBin = await findExecutable("claude");
      if (!claudeBin) {
        this.logger.info(
          "claude binary not found — skipping usage probe (Usage modal will show no data)",
        );
        return;
      }
      await fsPromises.mkdir(this.probeCwd, { recursive: true });
      // Clear any stale payload from a previous run.
      try {
        await fsPromises.rm(this.outputPath, { force: true });
      } catch {
        // ignore
      }
      this.startWatcher();
      this.spawnPty(claudeBin);
      this.ptyReadyAt = Date.now() + this.initialDelayMs;
      this.logger.info(
        { outputPath: this.outputPath, mode: "warm+on-demand" },
        "claude usage probe started",
      );
    } catch (err) {
      this.logger.warn({ err }, "failed to start claude usage probe");
    } finally {
      this.starting = false;
    }
  }

  /**
   * Trigger a single probe. Returns the resulting snapshot, or null if the
   * probe failed / timed out / no PTY available. Concurrent callers are
   * deduped — they all await the same in-flight probe.
   */
  async triggerProbe(): Promise<PlanUsageSnapshot | null> {
    if (this.stopped) return null;
    if (this.inflight) return this.inflight;
    if (!this.ptyProcess) {
      this.logger.debug("triggerProbe: no pty available");
      return null;
    }

    this.inflight = this.runProbe()
      .then((snapshot) => {
        if (snapshot) {
          this.lastSnapshot = snapshot;
          for (const listener of Array.from(this.listeners)) {
            try {
              listener(snapshot.rateLimits);
            } catch (err) {
              this.logger.debug({ err }, "usage-probe listener threw");
            }
          }
        }
        return snapshot;
      })
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }

  async stop(): Promise<void> {
    this.stopped = true;
    if (this.watcher) {
      try {
        this.watcher.close();
      } catch {
        // ignore
      }
      this.watcher = null;
    }
    if (this.ptyProcess) {
      const proc = this.ptyProcess;
      this.ptyProcess = null;
      try {
        proc.write("/exit\r");
      } catch {
        // ignore
      }
      setTimeout(() => {
        try {
          proc.kill();
        } catch {
          // ignore
        }
      }, 500);
    }
    if (this.pendingResolve) {
      this.pendingResolve(null);
      this.pendingResolve = null;
    }
  }

  private startWatcher(): void {
    const dir = path.dirname(this.outputPath);
    const filename = path.basename(this.outputPath);
    try {
      fs.mkdirSync(dir, { recursive: true });
      const watcher = fs.watch(dir, (_eventType, changed) => {
        if (changed !== filename) return;
        void this.onPayloadChange();
      });
      watcher.on("error", (err) => {
        this.logger.debug({ err }, "usage probe watcher error");
      });
      this.watcher = watcher;
    } catch (err) {
      this.logger.warn({ err }, "failed to start usage probe watcher");
    }
  }

  private spawnPty(claudeBin: string): void {
    const inlineSettings = JSON.stringify({
      statusLine: {
        type: "command",
        command: `node ${JSON.stringify(this.relayScriptPath)}`,
      },
    });
    // Note: we deliberately do NOT pass `--bare` here. `--bare` forces auth
    // to ANTHROPIC_API_KEY / apiKeyHelper and refuses to read OAuth /
    // keychain — which means OAuth subscribers (Claude Pro/Max users) would
    // launch a PTY that can never authenticate, never run a turn, and
    // therefore never fire the statusline hook this probe depends on.
    const args = [
      "--settings",
      inlineSettings,
      "--model",
      PROBE_MODEL,
      "--name",
      "appostle-usage-probe",
      "--dangerously-skip-permissions",
    ];
    try {
      const env: Record<string, string> = {
        ...(process.env as Record<string, string>),
        APPOSTLE_STATUSLINE_OUT: this.outputPath,
        CLAUDE_CODE_SANDBOXED: "1",
      };
      const ptyProcess = pty.spawn(claudeBin, args, {
        name: "xterm-256color",
        cols: 80,
        rows: 24,
        cwd: this.probeCwd,
        env,
      });
      ptyProcess.onExit(({ exitCode, signal }) => {
        this.ptyProcess = null;
        this.ptyReadyAt = null;
        if (this.stopped) return;
        this.logger.warn(
          { exitCode, signal },
          "claude usage probe pty exited unexpectedly — next triggerProbe will respawn",
        );
        // Best-effort respawn on next probe request.
        if (this.pendingResolve) {
          this.pendingResolve(null);
          this.pendingResolve = null;
        }
      });
      ptyProcess.onData(() => {
        // drain
      });
      this.ptyProcess = ptyProcess;
    } catch (err) {
      this.logger.warn({ err }, "failed to spawn claude usage probe pty");
    }
  }

  private async runProbe(): Promise<PlanUsageSnapshot | null> {
    const proc = this.ptyProcess;
    if (!proc) return null;

    // Wait for the PTY's initial settle window if we're probing very soon
    // after spawn.
    const readyAt = this.ptyReadyAt;
    if (readyAt && Date.now() < readyAt) {
      await new Promise((r) => setTimeout(r, readyAt - Date.now()));
    }

    // Set up a one-shot waiter BEFORE writing the prompt so we don't miss
    // a fast statusline write. A `resolved` flag ensures the waiter resolves
    // exactly once whether the statusline payload arrives, the timeout
    // fires, or the PTY exits.
    let resolved = false;
    const waiter = new Promise<PlanUsageSnapshot | null>((resolve) => {
      const timeout = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        this.pendingResolve = null;
        this.logger.warn({ timeoutMs: this.probeTimeoutMs }, "usage probe timed out");
        resolve(null);
      }, this.probeTimeoutMs);
      this.pendingResolve = (snapshot) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeout);
        resolve(snapshot);
      };
    });

    try {
      proc.write(`${PROBE_PROMPT}\r`);
    } catch (err) {
      this.logger.debug({ err }, "failed to write probe prompt");
      if (this.pendingResolve) {
        this.pendingResolve(null);
        this.pendingResolve = null;
      }
      return null;
    }

    return waiter;
  }

  private async onPayloadChange(): Promise<void> {
    try {
      const raw = await fsPromises.readFile(this.outputPath, "utf8");
      if (!raw) return;
      const json = JSON.parse(raw) as unknown;
      const rateLimits = parseStatuslinePayload(json);
      if (!rateLimits || rateLimits.length === 0) {
        // Statusline fired but rate_limits not yet populated — wait for the
        // next fire (claude emits multiple times per turn).
        return;
      }
      const snapshot: PlanUsageSnapshot = { rateLimits, capturedAt: Date.now() };
      this.lastSnapshot = snapshot;
      // If a probe is waiting, resolve it.
      if (this.pendingResolve) {
        const resolve = this.pendingResolve;
        this.pendingResolve = null;
        resolve(snapshot);
      } else {
        // Statusline fired outside of a probe (e.g. unsolicited). Still notify.
        for (const listener of Array.from(this.listeners)) {
          try {
            listener(rateLimits);
          } catch (err) {
            this.logger.debug({ err }, "usage-probe listener threw");
          }
        }
      }
      this.logger.debug({ count: rateLimits.length }, "usage probe captured rate limits");
    } catch (err) {
      const code = (err as NodeJS.ErrnoException | undefined)?.code;
      if (code !== "ENOENT") {
        this.logger.debug({ err }, "failed to read usage probe payload");
      }
    }
  }
}

let singleton: ClaudeUsageProbe | null = null;

export async function startClaudeUsageProbe(
  opts: ClaudeUsageProbeOptions,
): Promise<ClaudeUsageProbe> {
  if (!singleton) {
    singleton = new ClaudeUsageProbe(opts);
    await singleton.start();
  }
  return singleton;
}

export function getClaudeUsageProbe(): ClaudeUsageProbe | null {
  return singleton;
}

export async function stopClaudeUsageProbe(): Promise<void> {
  const current = singleton;
  singleton = null;
  if (current) {
    await current.stop();
  }
}
